import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { UploadError } from '@ff-os/shared-react/components/file-uploader/FileUploader';
import { Panel } from '@ff-os/shared-react/components/panel/Panel';
import { UploadMethodsWindow } from '@ff-os/shared-react/components/upload-methods/UploadMethodsWindow';
import { WindowBody } from '@ff-os/shared-react/components/window/partials/WindowBody';
import { Window } from '@ff-os/shared-react/components/window/Window';
import { selectFailedUploadsCount, selectSuccessfulUploadsCount } from '@ff-scene-plugins/photo-prints-redux/slices/groupsUiSlice';
import { selectPhotoIdsByGroupId } from '@ff-scene-plugins/photo-prints-redux/slices/photosSlice';
import { PhotoPrintsPdpDispatch, PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import { PhotoPrintsJson } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsJson';
import classNames from 'classnames';
import { usePhotoPrintsUploadMethods } from '../../../createPhotoPrintsUploadMethods';
import { applyMagicBorderThunk } from '../../../thunks/applyMagicBorderThunk';
import { uploadImages } from '../../../thunks/uploadImages';
import { incrementWithScheduledReset, updateWithScheduledReset } from '../../../thunks/withScheduledReset';
import { PhotoPrintsWindowErrors } from '../../PhotoPrintsWindowErrors';
import { PhotoPrintsGroupWindowNavigation } from './PhotoPrintsGroupWindowNavigation';
import { PhotoPrintsGroupWindowPanelBody } from './PhotoPrintsGroupWindowPanelBody';
import { PhotoPrintsGroupWindowPanelHeader } from './PhotoPrintsGroupWindowPanelHeader';
import styles from './PhotoPrintsGroupWindow.module.scss';

const _PhotoPrintsGroupWindow = ({
    photoIds, group, variantId,
    close, addPictures, onError,
    successfulUploadCount, failedUploadsCount, fotofun,
    isPhotoFunProcessing
}: Props) => {

    const { t } = useTranslation();
    const [isBrowseOpen, setIsBrowseOpen] = useState(false);

    const panelClassName = classNames(styles.panel, {
        [styles.success]: successfulUploadCount > 0,
        [styles.error]: failedUploadsCount > 0,
        [styles.rainbowGradient]: isPhotoFunProcessing
    });

    const uploadMethods = usePhotoPrintsUploadMethods({
        externalUpload: { groupId: group!.id },
        dropzone: { onChange: addPictures, onError }
    });

    const openBrowse = useCallback(() => setIsBrowseOpen(true), [setIsBrowseOpen]);
    const closeBrowse = useCallback(() => setIsBrowseOpen(false), [setIsBrowseOpen]);

    return (
        <Window close={close} fullHeight>
            <WindowBody className={styles.windowBody}>
                <Panel className={panelClassName}>

                    <PhotoPrintsGroupWindowPanelHeader
                        groupId={group!.id}
                        variantId={variantId} 
                        close={close} />

                    <PhotoPrintsGroupWindowPanelBody
                        groupId={group!.id}
                        browse={openBrowse}
                        close={close} />

                    <div className={styles.message}>
                        {failedUploadsCount > 0 && (
                            <>{t('photo_prints.photos_errored', { count: failedUploadsCount })} </>
                        )}

                        {successfulUploadCount > 0 && (
                            <>{t('photo_prints.photos_uploaded', { count: successfulUploadCount })}</>
                        )}
                    </div>
                </Panel>

                <UploadMethodsWindow
                    methods={uploadMethods}
                    isOpen={isBrowseOpen}
                    close={closeBrowse} />
            </WindowBody>

            <PhotoPrintsWindowErrors />
            <PhotoPrintsGroupWindowNavigation
                photoIds={photoIds}
                groupId={group!.id}
                browse={openBrowse}
                close={close}
                fotofun={fotofun}
                isPhotoFunProcessing={isPhotoFunProcessing} />
        </Window>
    );
};

type OwnProps = {
    close: () => void;
    groupId: string;
    variantId: string;
    fotofun?: boolean;
    isPhotoFunProcessing: boolean;
};

type Props = OwnProps & {
    photoIds: string[];
    addPictures: (files: File[]) => void;
    onError: (errors: UploadError[]) => void;
    applyMagicBorder: (magicBorderId: string|null) => void;
    successfulUploadCount: number;
    failedUploadsCount: number;
    photosWithoutFrame: PhotoPrintsJson.ProcessedPhoto[];
    allUploadsFinished: boolean;
    group: PhotoPrintsJson.Group | undefined;
};

const mapStateToProps = (state: PhotoPrintsPdpState, ownProps: OwnProps) => {

    const groupId = ownProps.groupId;
    const group = state.project.groups.entities[groupId];
    const photos = Object.values(state.project.photos.entities).filter(
        (p): p is PhotoPrintsJson.ProcessedPhoto =>
            !!p && p.groupId === groupId && p.processed && p.status === 'saved'
    );

    const photosWithoutFrame = group?.magicBorderId ? [] : photos;
    const successfulUploads = selectSuccessfulUploadsCount(state, groupId);
    const failedUploads = selectFailedUploadsCount(state, groupId);
    const allUploadsFinished = !!group && successfulUploads + failedUploads >= group.amountInQueue;

    return {
        photoIds: selectPhotoIdsByGroupId(state, ownProps.groupId),
        successfulUploadsCount: successfulUploads,
        failedUploadsCount: failedUploads,
        photosWithoutFrame,
        allUploadsFinished,
        group
    };
};

const mapDispatchToProps = (dispatch: PhotoPrintsPdpDispatch, ownProps: OwnProps) => ({
    addPictures: (files: File[]) => dispatch(uploadImages(files, ownProps.groupId)),
    onError: (errors: UploadError[]) => {
        dispatch(incrementWithScheduledReset(ownProps.groupId, 'failedUploadsCount', errors.length));
        dispatch(updateWithScheduledReset({ id: ownProps.groupId, key: 'failedUploadsError', value: errors }));
    },
    applyMagicBorder: (magicBorderId: string|null) => {
        dispatch(applyMagicBorderThunk(ownProps.groupId, magicBorderId));
    }
});

export const PhotoPrintsGroupWindow = connect(
    mapStateToProps,
    mapDispatchToProps
)(_PhotoPrintsGroupWindow);